/**
 * Retention models on the Churn page: the same subscriptions read several
 * ways, picked like attribution models (services/retentionModels on the
 * server does the statistics; this only draws them).
 */
import { useState } from 'react'
import { Info } from 'lucide-react'
import {
  Area, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'

const COMPARE = 'compare'
const COMPARED = ['kaplan_meier', 'complete_cohorts', 'weibull']
const COLORS = { kaplan_meier: '#2563eb', complete_cohorts: '#16a34a', weibull: '#9333ea', involuntary: '#dc2626', voluntary: '#d97706', total: '#475569' }

const pct = value => (value == null ? '—' : `${(value * 100).toFixed(1)}%`)
const range = (lower, upper) => (lower == null || upper == null ? '—' : `${(lower * 100).toFixed(1)}–${(upper * 100).toFixed(1)}%`)
const count = value => (value == null ? '—' : value.toLocaleString('en-US'))
const tooltipStyle = { backgroundColor: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }

export default function RetentionModels({ data }) {
  const [selected, setSelected] = useState('renewal_rates')
  if (!data?.models?.length) return null
  const lowN = data.low_n || 100
  const model = data.models.find(m => m.id === selected)

  return (
    <Card>
      <CardHeader className="space-y-4">
        <div>
          <CardTitle className="text-lg">Retention models</CardTitle>
          <CardDescription className="max-w-3xl">
            Same subscribers, different reads. Each model answers a slightly different question; the info button explains how it works and when to trust it.
            Figures resting on fewer than {lowN} subscribers are greyed out.
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">Model
            <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={selected} onChange={event => setSelected(event.target.value)}>
              {data.models.map(m => <option key={m.id} value={m.id}>{m.label}{m.available ? '' : ' (not yet available)'}</option>)}
              <option value={COMPARE}>Compare curves</option>
            </select>
          </label>
          {model && <ModelDetails model={model} />}
        </div>
      </CardHeader>
      <CardContent>
        {selected === COMPARE
          ? <CompareCurves models={data.models.filter(m => COMPARED.includes(m.id) && m.available)} />
          : model && (model.available ? <ModelView model={model} lowN={lowN} /> : <p className="text-sm text-amber-600">{model.unavailable_reason}</p>)}
      </CardContent>
    </Card>
  )
}

function ModelDetails({ model }) {
  const { details } = model
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-sm hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={`How ${model.label} works`}>
          How it works<Info className="h-4 w-4" aria-hidden="true" />
        </button>
      </DialogTrigger>
      <DialogContent className="w-[calc(100%_-_2rem)] max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="pr-6">{model.label}</DialogTitle>
          <DialogDescription>{details.use}</DialogDescription>
        </DialogHeader>
        <section className="space-y-2 text-sm"><h3 className="font-semibold">How it is calculated</h3><p className="text-muted-foreground">{details.rule}</p></section>
        <section className="rounded-md border bg-muted/30 p-4 space-y-2 text-sm"><h3 className="font-semibold">Example</h3><p className="text-muted-foreground">{details.example}</p></section>
        <section className="space-y-2 text-sm"><h3 className="font-semibold">What to keep in mind</h3><p className="text-muted-foreground">{details.caveat}</p></section>
      </DialogContent>
    </Dialog>
  )
}

export function ModelView({ model, lowN }) {
  switch (model.kind) {
    case 'curve': return <CurveView model={model} lowN={lowN} />
    case 'renewals': return <RenewalsView plans={model.data.plans} lowN={lowN} />
    case 'cif': return <CifView curve={model.data.curve} lowN={lowN} />
    case 'projection': return <ProjectionView plans={model.data.plans} lowN={lowN} />
    case 'hazard_ratios': return <HazardRatiosView data={model.data} />
    case 'cohorts': return <CohortsView data={model.data} lowN={lowN} />
    default: return null
  }
}

const dim = (n, lowN) => (n < lowN ? 'text-muted-foreground/60' : '')
const cell = 'p-2 text-right font-mono text-sm'
const head = 'p-2 text-right font-medium text-sm'

function Table({ children }) {
  return <div className="border rounded-lg overflow-x-auto"><table className="w-full">{children}</table></div>
}

function CurveView({ model, lowN }) {
  const curve = model.data.curve.filter(point => point.value != null)
  const chart = curve.map(point => ({ ...point, band: [point.lower, point.upper] }))
  return (
    <div className="space-y-4">
      {model.id === 'weibull' && (
        <p className="text-sm text-muted-foreground">Shape {model.data.shape} ({model.data.shape < 1 ? 'churn risk falls with tenure' : 'churn risk rises with tenure'}), scale {count(model.data.scale_days)} days, fitted to {count(model.data.events)} churns.</p>
      )}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="day" tickFormatter={day => `Day ${day}`} />
            <YAxis domain={[0, 1]} tickFormatter={value => `${Math.round(value * 100)}%`} />
            <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => (Array.isArray(value) ? [range(value[0], value[1]), '95% range'] : [pct(value), 'Retained'])} labelFormatter={day => `Day ${day}`} />
            <Area dataKey="band" stroke="none" fill={COLORS[model.id] || '#2563eb'} fillOpacity={0.15} isAnimationActive={false} />
            <Line dataKey="value" stroke={COLORS[model.id] || '#2563eb'} strokeWidth={2} dot isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <Table>
        <thead className="bg-muted/50"><tr><th className="p-2 text-left font-medium text-sm">Day</th><th className={head}>Retained</th><th className={head}>95% range</th><th className={head}>Resting on</th></tr></thead>
        <tbody>{model.data.curve.map(point => (
          <tr key={point.day} className={`border-t ${dim(point.n, lowN)}`}>
            <td className="p-2 text-sm">Day {point.day}{point.extrapolated && <span className="ml-1 text-xs text-amber-600">extrapolated</span>}</td>
            <td className={cell}>{pct(point.value)}</td><td className={cell}>{range(point.lower, point.upper)}</td><td className={cell}>{count(point.n)}</td>
          </tr>
        ))}</tbody>
      </Table>
    </div>
  )
}

function RenewalsView({ plans, lowN }) {
  return (
    <div className="space-y-6">
      {plans.map(plan => (
        <div key={plan.plan} className="space-y-2">
          <h4 className="text-sm font-semibold">{plan.label} <span className="font-normal text-muted-foreground">· {count(plan.subscriptions)} subscriptions · renews every {plan.cycle_days} days</span></h4>
          {plan.renewals.length === 0
            ? <p className="text-sm text-muted-foreground">No subscriber has reached a renewal yet.</p>
            : (
              <Table>
                <thead className="bg-muted/50"><tr>
                  <th className="p-2 text-left font-medium text-sm">Renewal</th><th className={head}>Reached</th><th className={head}>Churned at it</th>
                  <th className={head}>Renewal rate</th><th className={head}>95% range</th><th className={head}>Cumulative retention</th>
                </tr></thead>
                <tbody>{plan.renewals.map(row => (
                  <tr key={row.renewal} className={`border-t ${dim(row.reached, lowN)}`}>
                    <td className="p-2 text-sm">{ordinal(row.renewal)}</td><td className={cell}>{count(row.reached)}</td><td className={cell}>{count(row.churned)}</td>
                    <td className={cell}>{pct(row.rate)}</td><td className={cell}>{range(row.rate_lower, row.rate_upper)}</td><td className={cell}>{pct(row.cumulative)}</td>
                  </tr>
                ))}</tbody>
              </Table>
            )}
        </div>
      ))}
    </div>
  )
}

function CifView({ curve, lowN }) {
  const points = curve.filter(point => point.total != null)
  return (
    <div className="space-y-4">
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="day" tickFormatter={day => `Day ${day}`} />
            <YAxis tickFormatter={value => `${Math.round(value * 100)}%`} />
            <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => [pct(value), name]} labelFormatter={day => `Day ${day}`} />
            <Legend />
            <Line name="Involuntary" dataKey="involuntary" stroke={COLORS.involuntary} strokeWidth={2} isAnimationActive={false} />
            <Line name="Voluntary" dataKey="voluntary" stroke={COLORS.voluntary} strokeWidth={2} isAnimationActive={false} />
            <Line name="All churn" dataKey="total" stroke={COLORS.total} strokeDasharray="4 3" isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <Table>
        <thead className="bg-muted/50"><tr><th className="p-2 text-left font-medium text-sm">Day</th><th className={head}>Involuntary</th><th className={head}>Voluntary</th><th className={head}>Reason unknown</th><th className={head}>All churn</th><th className={head}>Resting on</th></tr></thead>
        <tbody>{curve.map(point => (
          <tr key={point.day} className={`border-t ${dim(point.n, lowN)}`}>
            <td className="p-2 text-sm">Day {point.day}</td><td className={cell}>{pct(point.involuntary)}</td><td className={cell}>{pct(point.voluntary)}</td>
            <td className={cell}>{pct(point.unknown)}</td><td className={cell}>{pct(point.total)}</td><td className={cell}>{count(point.n)}</td>
          </tr>
        ))}</tbody>
      </Table>
    </div>
  )
}

function ProjectionView({ plans, lowN }) {
  return (
    <div className="space-y-6">
      {plans.map(plan => (
        <div key={plan.plan} className="space-y-2">
          <h4 className="text-sm font-semibold">{plan.label} <span className="font-normal text-muted-foreground">· {count(plan.subscriptions)} subscriptions</span></h4>
          {!plan.available ? <p className="text-sm text-muted-foreground">{plan.unavailable_reason}</p> : (
            <>
              <p className="text-sm text-muted-foreground">
                Fitted to {plan.observed_renewals} observed renewals (α {plan.alpha}, β {plan.beta}). Expected renewals paid, through 36: {plan.expected_renewals_36}.
              </p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={plan.renewals.map(row => ({ ...row, band: [row.lower, row.upper] }))}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="renewal" tickFormatter={k => `R${k}`} />
                    <YAxis domain={[0, 1]} tickFormatter={value => `${Math.round(value * 100)}%`} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => (Array.isArray(value) ? [range(value[0], value[1]), '95% range'] : [pct(value), name])} labelFormatter={k => `After renewal ${k}`} />
                    <Legend />
                    <Area name="95% range" dataKey="band" stroke="none" fill="#2563eb" fillOpacity={0.15} isAnimationActive={false} legendType="none" />
                    <Line name="Projected" dataKey="projected" stroke="#2563eb" strokeDasharray="5 4" strokeWidth={2} isAnimationActive={false} />
                    <Line name="Observed" dataKey="observed" stroke="#16a34a" strokeWidth={2} isAnimationActive={false} connectNulls={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <Table>
                <thead className="bg-muted/50"><tr><th className="p-2 text-left font-medium text-sm">After renewal</th><th className={head}>Observed</th><th className={head}>Projected</th><th className={head}>95% range</th><th className={head}>Reached</th></tr></thead>
                <tbody>{plan.renewals.map(row => (
                  <tr key={row.renewal} className={`border-t ${row.observed == null ? '' : dim(row.reached, lowN)}`}>
                    <td className="p-2 text-sm">{ordinal(row.renewal)}</td><td className={cell}>{pct(row.observed)}</td><td className={cell}>{pct(row.projected)}</td>
                    <td className={cell}>{range(row.lower, row.upper)}</td><td className={cell}>{row.reached ? count(row.reached) : '—'}</td>
                  </tr>
                ))}</tbody>
              </Table>
            </>
          )}
        </div>
      ))}
    </div>
  )
}

function HazardRatiosView({ data }) {
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        {count(data.subscriptions)} subscriptions, {count(data.events)} churns. Below 1: churns less than the comparison group; above 1: more.
        {data.channel_reference && <> Channels are compared with {data.channel_reference.label}, the largest attributed channel.</>}
        {data.channels_read === false && <> Acquisition channel could not be read, so it is left out.</>}
      </p>
      <Table>
        <thead className="bg-muted/50"><tr><th className="p-2 text-left font-medium text-sm">Trait</th><th className={head}>Subscribers with it</th><th className={head}>Hazard ratio</th><th className={head}>95% range</th><th className={head}>p</th><th className="p-2 text-left font-medium text-sm">Reads as</th></tr></thead>
        <tbody>{data.coefficients.map(row => (
          <tr key={row.key} className="border-t">
            <td className="p-2 text-sm">{row.label}</td><td className={cell}>{count(row.exposed)}</td><td className={cell}>{row.hazard_ratio}</td>
            <td className={cell}>{row.lower}–{row.upper}</td><td className={cell}>{row.p_value < 0.001 ? '<0.001' : row.p_value}</td>
            <td className="p-2 text-sm text-muted-foreground">{hazardSentence(row)}</td>
          </tr>
        ))}</tbody>
      </Table>
    </div>
  )
}

function hazardSentence(row) {
  if (row.lower <= 1 && row.upper >= 1) return 'No clear difference'
  const change = Math.round(Math.abs(row.hazard_ratio - 1) * 100)
  return `${change}% ${row.hazard_ratio < 1 ? 'less' : 'more'} churn risk`
}

function CohortsView({ data, lowN }) {
  return (
    <Table>
      <thead className="bg-muted/50"><tr>
        <th className="p-2 text-left font-medium text-sm">Signup month</th><th className={head}>Size</th>
        {data.points.map(day => <th key={day} className={head}>Day {day}</th>)}
      </tr></thead>
      <tbody>
        <tr className="border-t bg-muted/30">
          <td className="p-2 text-sm font-medium">Pooled</td><td className={cell} />
          {data.points.map(day => <td key={day} className={cell}>{pct(data.priors[day]?.pooled)}</td>)}
        </tr>
        {data.cohorts.map(cohort => (
          <tr key={cohort.cohort_month} className="border-t">
            <td className="p-2 text-sm">{cohort.cohort_month}</td><td className={cell}>{count(cohort.size)}</td>
            {data.points.map(day => {
              const value = cohort.values[day]
              if (!value) return <td key={day} className={`${cell} text-muted-foreground`}>—</td>
              return (
                <td key={day} className={`${cell} ${dim(value.n, lowN)}`} title={`Raw ${pct(value.raw)} of ${count(value.n)}; 95% credible ${range(value.lower, value.upper)}`}>
                  {pct(value.value)}<span className="block text-xs text-muted-foreground">raw {pct(value.raw)} · n {count(value.n)}</span>
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

function CompareCurves({ models }) {
  if (!models.length) return <p className="text-sm text-muted-foreground">No day-based curve is available to compare.</p>
  const days = [...new Set(models.flatMap(m => m.data.curve.map(point => point.day)))].sort((a, b) => a - b)
  const rows = days.map(day => Object.fromEntries([['day', day], ...models.map(m => [m.id, m.data.curve.find(point => point.day === day)?.value ?? null])]))
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">The day-based models on one chart. Where they agree the figure is robust; where they part, the difference is what the models assume.</p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="day" tickFormatter={day => `Day ${day}`} />
            <YAxis domain={[0, 1]} tickFormatter={value => `${Math.round(value * 100)}%`} />
            <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => [pct(value), name]} labelFormatter={day => `Day ${day}`} />
            <Legend />
            {models.map(m => <Line key={m.id} name={m.label} dataKey={m.id} stroke={COLORS[m.id]} strokeWidth={2} isAnimationActive={false} connectNulls={false} />)}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <Table>
        <thead className="bg-muted/50"><tr><th className="p-2 text-left font-medium text-sm">Day</th>{models.map(m => <th key={m.id} className={head}>{m.label}</th>)}</tr></thead>
        <tbody>{rows.map(row => (
          <tr key={row.day} className="border-t"><td className="p-2 text-sm">Day {row.day}</td>{models.map(m => <td key={m.id} className={cell}>{pct(row[m.id])}</td>)}</tr>
        ))}</tbody>
      </Table>
    </div>
  )
}

function ordinal(n) {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th'
  return `${n}${suffix}`
}

